/* ============================================================================
   RTB collection pages: behaviour for sections/rtb-collection.liquid on the
   Shop by Age, Shop by Category, Brand and default collection templates.

   LOAD. Classic deferred script from layout/theme.liquid, like the other RTB
   scripts. Where it needs a Horizon module (@theme/section-renderer,
   @theme/utilities, @shopify/events) it reaches for it with a dynamic
   `import()` through the import map in snippets/scripts.liquid, at the moment
   of use, and every import has a degradation if it fails. The focus trap,
   dialog semantics, return focus, announcements and section-unload teardown
   come from assets/rtb-a11y.js (window.RTB.a11y), loaded just before it.

   THREE JOBS.

   1. <rtb-filter-bar>: the filter disclosures. One panel at a time; Escape,
      the trigger, a scrim click or focus leaving the panel closes it. At 750px
      and up the panel is a disclosure (no trap, no inert, no aria-modal).
      Below 750px the same panel is a full-screen sheet, which is modal, so it
      gets role="dialog", aria-modal, a name, a focus trap and Horizon's scroll
      lock while open. The breakpoint matches the `max-width: 749px` block in
      assets/rtb-collection.css.

   2. Filtering, sort and Back/Forward. Filters apply on change (there is no
      Apply button). Each change pushes one history entry and re-renders the
      results section through `sectionRenderer.renderSection()`, which morphs.
      Horizon has no popstate re-render, so this file re-renders the section
      for the popped URL and re-syncs the controls, because a morph does not
      move a dirty checkbox's `checked` property. If a render fails the page
      navigates to the URL, so the address bar and the grid never disagree.

   3. Add to cart from a catalogue card, plus the announcements for 2 and 3.
      Single-variant products post to /cart/add.js and dispatch Horizon's
      CartLinesUpdateEvent, which refreshes the bubble and the drawer.
      Multi-option products go to the product page; the card never posts a
      variant the shopper did not choose. Focus stays on the button.

   MARKUP CONTRACT (snippets/rtb-collection-filters.liquid,
   snippets/rtb-catalogue-card.liquid, sections/rtb-collection.liquid):

     <rtb-filter-bar data-section-id data-results-url>
       <form data-rtb-filter-form>
         <button data-rtb-filter-trigger id aria-expanded aria-controls>
           <svg data-rtb-filter-chevron="closed"> <svg data-rtb-filter-chevron="open" hidden>
         <div id data-rtb-filter-panel data-sheet-label hidden>
           <button data-rtb-filter-close>
           <input name value data-rtb-filter-url-add data-rtb-filter-url-remove>
       <div data-rtb-filter-scrim hidden>
     <nav data-page-param data-current-page>          pagination
     <p class="rtb-collection__count" role="status">   result count
     <div data-rtb-cart-status role="status"
          data-label-adding data-label-added data-label-error>
     <button data-rtb-add data-product-url data-product-title
             [data-variant-id] [data-needs-options]>

   No JavaScript: every trigger is a real button over a `hidden` panel, the
   form is a real GET, and the card's image and title are one link to the
   product page.

   Nothing here animates. `data-added` and `aria-busy` drive CSS that is
   dropped under prefers-reduced-motion; `data-added` is cleared on a timer
   because `animationend` never fires when the animation is `none`.
   ============================================================================ */

(function () {
  'use strict';

  if (customElements.get('rtb-filter-bar')) return;

  /* Marker read by the <noscript> fallback in rtb-collection-filters.liquid.
     A section render can move that <noscript>'s <style> into the live
     document, so its rules are scoped to `:root:not([data-rtb-js])`. It sits
     on <html> because <html> is outside every section a morph touches. */
  document.documentElement.setAttribute('data-rtb-js', '');

  var a11y = window.RTB.a11y;

  var SHEET_QUERY = '(max-width: 749px)';

  /* Horizon's own add-to-cart timings (assets/product-form.js). */
  var ADDED_MS = 800;
  var SUCCESS_MS = 5000;
  var ERROR_MS = 10000;

  /* StandardEvents.cartLinesUpdate, used as a plain event name when the
     @shopify/events module cannot be loaded. */
  var EVT_CART_LINES_UPDATE = 'shopify:cart:lines-update';

  /* Set `window.rtbDebug = true` in the console to see swallowed failures. */
  function debug(message, error) {
    if (window.rtbDebug) console.warn('[rtb-collection] ' + message, error);
  }

  /* ==========================================================================
     Module loading. Each specifier is imported once; a rejection is cached as
     `null` so a failed import is not retried on every interaction.
     ========================================================================== */

  var moduleCache = {};

  function loadModule(specifier) {
    if (!moduleCache[specifier]) {
      moduleCache[specifier] = import(specifier).catch(function (error) {
        debug('could not load ' + specifier, error);
        return null;
      });
    }
    return moduleCache[specifier];
  }

  /* ==========================================================================
     Helpers
     ========================================================================== */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function collectionRoot() {
    return document.querySelector('.rtb-collection');
  }

  /** The results section's id: stated on the filter bar, else the wrapper's. */
  function sectionIdFor(element) {
    if (!element || !element.closest) return null;

    var bar = element.querySelector('rtb-filter-bar[data-section-id]') || element.closest('rtb-filter-bar[data-section-id]');
    if (bar) return bar.getAttribute('data-section-id');

    var section = element.closest('.shopify-section');
    return section && section.id ? section.id : null;
  }

  /** Path plus sorted query, no hash: two URLs that differ only in parameter
      order or fragment describe the same result set. */
  function normalizeUrl(href) {
    try {
      var url = new URL(href, window.location.href);
      url.searchParams.sort();
      return url.pathname + url.search;
    } catch (error) {
      return String(href);
    }
  }

  /** The section's cart live region, looked up live because a render may have
      replaced it. */
  function cartStatusRegion(context) {
    var scope = (context && context.closest && context.closest('.shopify-section')) || document;
    return scope.querySelector('[data-rtb-cart-status]') || document.querySelector('[data-rtb-cart-status]');
  }

  /** Translated strings, published by the section on its cart region. */
  function cartLabel(context, name) {
    var region = cartStatusRegion(context);
    return (region && region.getAttribute('data-label-' + name)) || '';
  }

  function resultsCountRegion() {
    return document.querySelector('.rtb-collection__count');
  }

  /* ==========================================================================
     Pagination contract: the nav publishes `paginate.page_param` and
     `paginate.current_page`, so the parameter name is never assumed. No nav
     means a single page, where 'page' is a safe fallback.
     ========================================================================== */

  function paginationNav() {
    var root = collectionRoot() || document;
    return root.querySelector('[data-page-param]');
  }

  function pageParamName() {
    var nav = paginationNav();
    var name = nav ? nav.getAttribute('data-page-param') : null;
    return name || 'page';
  }

  function currentPageNumber() {
    var nav = paginationNav();
    var value = nav ? parseInt(nav.getAttribute('data-current-page'), 10) : NaN;
    return isNaN(value) || value < 1 ? 1 : value;
  }

  /** A new result set starts on page 1. Mutates and returns `url`. */
  function dropPageParam(url) {
    var name = pageParamName();
    if (currentPageNumber() === 1 && !url.searchParams.has(name)) return url;
    url.searchParams.delete(name);
    return url;
  }

  /* ==========================================================================
     Results rendering, shared by the filter bar and popstate.
     ========================================================================== */

  /* The URL whose results are on screen. Advanced only after a render
     succeeds, so popstate can skip entries that are already showing. */
  var renderedUrl = normalizeUrl(window.location.href);

  /* Every render takes a number; only the newest one may clear busy, announce
     or fall back to a navigation. */
  var renderSequence = 0;

  function setGridBusy(scope, busy) {
    var grid = (scope || document).querySelector('.rtb-collection__grid, [ref="grid"]');
    if (grid) grid.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /* The count line is itself a role="status" region. If the morph keeps the
     node and changes its text, it announces on its own; if the morph replaces
     the node, a region that arrives populated says nothing, so the text goes
     out through the cart region instead. Never both. */
  function beginResultsWatch() {
    var region = resultsCountRegion();
    return { element: region, text: region ? region.textContent : null };
  }

  function finishResultsWatch(before) {
    var region = resultsCountRegion();
    if (!region) return;

    var text = (region.textContent || '').trim();
    if (!text) return;

    var preserved = before && before.element === region && region.isConnected;
    if (preserved) return;

    a11y.announce(cartStatusRegion(region), text);
  }

  /**
   * Re-renders the results section for `href`.
   * @param {string} href
   * @param {{restoreControls?: boolean}} [options]
   * @returns {Promise<boolean>} true when this render landed and is the newest
   */
  function renderResults(href, options) {
    var settings = options || {};
    var root = collectionRoot();
    if (!root) return Promise.resolve(false);

    var sectionId = sectionIdFor(root);
    if (!sectionId) return Promise.resolve(false);

    var sequence = ++renderSequence;
    var target = normalizeUrl(href);
    var watch = beginResultsWatch();
    setGridBusy(root, true);

    return loadModule('@theme/section-renderer')
      .then(function (module) {
        if (!module || !module.sectionRenderer) throw new Error('section renderer unavailable');
        // renderSection mutates the URL it is given, so it gets a fresh one.
        return module.sectionRenderer.renderSection(sectionId, { url: new URL(href, window.location.href) });
      })
      .then(
        function () {
          return true;
        },
        function (error) {
          if (sequence !== renderSequence) return false;
          /* The URL is already in the address bar, so a full navigation is
             the only way to make the grid match it. */
          debug('section render failed, navigating', error);
          window.location.assign(href);
          return false;
        }
      )
      .then(function (landed) {
        if (sequence !== renderSequence) return false;

        var freshRoot = collectionRoot();
        setGridBusy(freshRoot, false);
        if (!landed) return false;

        renderedUrl = target;
        if (settings.restoreControls) restoreControls(freshRoot);
        finishResultsWatch(watch);
        return true;
      });
  }

  /* A morph reconciles attributes, but a checkbox the user has clicked no
     longer follows its `checked` attribute. Controls are re-synced from the
     URL by name + value, the pairing `filter_value.param_name` /
     `filter_value.value` produces. */
  function restoreControls(scope) {
    var root = scope || collectionRoot();
    if (!root) return;

    var params = new URL(window.location.href).searchParams;

    toArray(root.querySelectorAll('input[name]')).forEach(function (control) {
      var name = control.getAttribute('name');
      var type = (control.getAttribute('type') || '').toLowerCase();

      // Hidden and button-like inputs carry markup decisions, not filter state.
      if (type === 'hidden' || type === 'submit' || type === 'button' || type === 'reset' || type === 'image') return;

      if (type === 'checkbox' || type === 'radio') {
        var wanted = params.getAll(name).indexOf(control.value) !== -1;
        if (control.checked !== wanted) control.checked = wanted;
        return;
      }

      control.value = params.get(name) || '';
    });
  }

  /* ==========================================================================
     <rtb-filter-bar>
     ========================================================================== */

  class RtbFilterBar extends HTMLElement {
    connectedCallback() {
      this.openTrigger = null;
      this.returnFocusTo = null;
      this.ownsScrollLock = false;

      this.sheet = window.matchMedia(SHEET_QUERY);

      this._onClick = this._handleClick.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
      this._onFocusout = this._handleFocusout.bind(this);
      this._onChange = this._handleChange.bind(this);
      this._onDocumentClick = this._handleDocumentClick.bind(this);
      this._onBreakpoint = this._handleBreakpoint.bind(this);

      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('focusout', this._onFocusout);
      this.addEventListener('change', this._onChange);
      document.addEventListener('click', this._onDocumentClick);
      this._offSectionUnload = a11y.onSectionUnload(this, this.disconnectedCallback.bind(this));
      this.sheet.addEventListener('change', this._onBreakpoint);

      /* The scrim may be a sibling: it is `position: fixed` and must not
         inherit the bar's stacking context. */
      var section = this.closest('.shopify-section') || document;
      this.scrim = this.querySelector('[data-rtb-filter-scrim]') || section.querySelector('[data-rtb-filter-scrim]');
      if (this.scrim) {
        this._onScrim = this.closeOpenPanel.bind(this, { focusTrigger: true });
        this.scrim.addEventListener('click', this._onScrim);
        this.scrim.setAttribute('aria-hidden', 'true');
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('focusout', this._onFocusout);
      this.removeEventListener('change', this._onChange);
      document.removeEventListener('click', this._onDocumentClick);
      if (this._offSectionUnload) this._offSectionUnload();
      this._offSectionUnload = null;
      if (this.sheet) this.sheet.removeEventListener('change', this._onBreakpoint);
      if (this.scrim && this._onScrim) this.scrim.removeEventListener('click', this._onScrim);

      // Never leave the document locked behind a removed sheet.
      this._releaseScrollLock();
      this.openTrigger = null;
    }

    /* --- Panels ----------------------------------------------------------- */

    panelFor(trigger) {
      var id = trigger && trigger.getAttribute('aria-controls');
      if (!id) return null;
      var selector = '#' + CSS.escape(id);
      var section = this.closest('.shopify-section') || document;
      return this.querySelector(selector) || section.querySelector(selector);
    }

    get isSheet() {
      return !!(this.sheet && this.sheet.matches);
    }

    openPanel(trigger, options) {
      var settings = options || {};
      var panel = this.panelFor(trigger);
      if (!panel) return;

      // One at a time; opening a second one leaves focus where the user is going.
      if (this.openTrigger && this.openTrigger !== trigger) this.closeOpenPanel({ focusTrigger: false });

      this.openTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      this._setChevron(trigger, true);
      panel.hidden = false;
      if (this.scrim) this.scrim.hidden = false;
      this.setAttribute('data-panel-open', '');

      if (!this.isSheet) return;

      /* Dialog semantics only on the sheet: the same element is a non-modal
         disclosure at 750px and up, where role="dialog" would be false. */
      if (!settings.keepFocus) this.returnFocusTo = document.activeElement;
      a11y.setDialog(panel, panel.getAttribute('data-sheet-label') || (trigger.textContent || '').trim());
      this._acquireScrollLock();

      if (settings.keepFocus) return;

      var first = panel.querySelector('[data-rtb-filter-close]') || panel.querySelector(a11y.FOCUSABLE);
      if (first) first.focus();
    }

    /* The CSS swaps the chevron box size on aria-expanded; the glyph swap is
       `hidden` on the two SVGs the markup ships. */
    _setChevron(trigger, open) {
      var closed = trigger.querySelector('[data-rtb-filter-chevron="closed"]');
      var opened = trigger.querySelector('[data-rtb-filter-chevron="open"]');
      if (closed) closed.hidden = !!open;
      if (opened) opened.hidden = !open;
    }

    closeOpenPanel(options) {
      var settings = options || {};
      var trigger = this.openTrigger;
      if (!trigger) return;

      var panel = this.panelFor(trigger);

      this.openTrigger = null;
      this.removeAttribute('data-panel-open');
      trigger.setAttribute('aria-expanded', 'false');
      this._setChevron(trigger, false);
      if (this.scrim) this.scrim.hidden = true;

      if (panel) {
        panel.hidden = true;
        a11y.clearDialog(panel);
      }

      this._releaseScrollLock();

      if (!settings.focusTrigger) {
        this.returnFocusTo = null;
        return;
      }

      /* Return focus to where the sheet was opened from, if that is still a
         visible, focusable element; otherwise to the trigger. A pointer open
         leaves <body> as the active element, whose focus() does nothing. */
      a11y.returnFocus(this.isSheet ? this.returnFocusTo : null, trigger);
      this.returnFocusTo = null;
    }

    /* --- Scroll lock: Horizon's owner-counted lock (@theme/utilities), so the
       sheet, the mobile nav and Horizon's drawers never release each other's
       lock. Both calls chain on the same cached import, so a lock and an
       unlock issued in that order also run in that order. ------------------ */

    _acquireScrollLock() {
      if (this.ownsScrollLock) return;
      this.ownsScrollLock = true;
      var owner = this;
      loadModule('@theme/utilities').then(function (utilities) {
        if (utilities && utilities.lockScroll) utilities.lockScroll(owner);
      });
    }

    _releaseScrollLock() {
      if (!this.ownsScrollLock) return;
      this.ownsScrollLock = false;
      var owner = this;
      loadModule('@theme/utilities').then(function (utilities) {
        if (utilities && utilities.unlockScroll) utilities.unlockScroll(owner);
      });
    }

    /* --- Events ----------------------------------------------------------- */

    _handleClick(event) {
      var target = event.target;

      if (target.closest('[data-rtb-filter-close]')) {
        event.preventDefault();
        this.closeOpenPanel({ focusTrigger: true });
        return;
      }

      var trigger = target.closest('[data-rtb-filter-trigger]');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        if (this.openTrigger === trigger) this.closeOpenPanel({ focusTrigger: true });
        else this.openPanel(trigger);
        return;
      }

      /* A facet link inside a panel is upgraded to a morph. Modified clicks,
         other targets and other documents are left to the browser. */
      var link = target.closest('[data-rtb-filter-panel] a[href]');
      if (!link || !this.contains(link)) return;
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.button && event.button !== 0) return;
      if (link.target && link.target !== '_self') return;

      var destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin) return;
      if (destination.pathname !== window.location.pathname) return;

      event.preventDefault();
      this._apply(destination.toString(), { closePanel: true });
    }

    _handleKeydown(event) {
      if (event.key === 'Tab' && this.openTrigger && this.isSheet) {
        this._trapFocus(event);
        return;
      }
      if (event.key !== 'Escape' || !this.openTrigger) return;

      event.stopPropagation();
      this.closeOpenPanel({ focusTrigger: true });
    }

    /* Focus leaving the disclosure closes it and moves nothing, so the user is
       not pulled back from wherever they tabbed to. */
    _handleFocusout(event) {
      if (!this.openTrigger || this.isSheet) return;

      var next = event.relatedTarget;
      // Focus left the document (window blur, devtools): not a close.
      if (!next) return;

      var panel = this.panelFor(this.openTrigger);
      if (panel && panel.contains(next)) return;
      if (this.openTrigger.contains(next)) return;

      this.closeOpenPanel({ focusTrigger: false });
    }

    _handleDocumentClick(event) {
      if (!this.openTrigger) return;
      if (this.contains(event.target)) return;
      var panel = this.panelFor(this.openTrigger);
      if (panel && panel.contains(event.target)) return;
      if (this.scrim && this.scrim === event.target) return; // its own handler
      this.closeOpenPanel({ focusTrigger: false });
    }

    /* A sheet stranded on a desktop viewport, or a dropdown holding a scroll
       lock, is worse than a closed panel. */
    _handleBreakpoint() {
      this.closeOpenPanel({ focusTrigger: false });
    }

    /* Filters apply on change; there is no draft state. */
    _handleChange(event) {
      var control = event.target;
      if (!control || !control.name) return;

      // Sort (radio) closes on pick; a multi-select facet stays open.
      var type = (control.getAttribute('type') || '').toLowerCase();
      if (type === 'radio') this.closeOpenPanel({ focusTrigger: true });

      /* Facet URLs come from the platform (`url_to_add` / `url_to_remove`),
         never concatenated: they carry GIDs, the sort and every other active
         facet. Sort and price have no such pair and go through the form. */
      var facetUrl = control.getAttribute(control.checked ? 'data-rtb-filter-url-add' : 'data-rtb-filter-url-remove');

      var href;
      if (facetUrl) {
        href = dropPageParam(new URL(facetUrl, window.location.href)).toString();
      } else {
        var form = control.form || this.querySelector('form');
        if (!form) return;
        href = this._urlFromForm(form);
      }

      this._apply(href, { closePanel: false, control: control });
    }

    /* Serialises the form the way Horizon's facets.js does: empty price bounds
       are dropped (they would pin the range to zero), and so is the page. The
       base is the route the markup declared, if it is same-origin. */
    _urlFromForm(form) {
      var params = new URLSearchParams(new FormData(form));

      if (params.get('filter.v.price.gte') === '') params.delete('filter.v.price.gte');
      if (params.get('filter.v.price.lte') === '') params.delete('filter.v.price.lte');
      params.delete(pageParamName());

      var url;
      try {
        url = new URL(this.getAttribute('data-results-url') || form.action || window.location.href, window.location.href);
        if (url.origin !== window.location.origin) url = new URL(window.location.href);
      } catch (error) {
        url = new URL(window.location.href);
      }

      url.search = params.toString();
      return url.toString();
    }

    _apply(href, options) {
      var settings = options || {};
      var control = settings.control || null;

      /* Captured before the close clears it: a morph can replace the trigger
         node that had focus, and only its id survives. */
      var closedTriggerId = settings.closePanel && this.openTrigger ? this.openTrigger.id : null;
      if (settings.closePanel) this.closeOpenPanel({ focusTrigger: true });

      /* One history entry per interaction, so Back undoes exactly one change.
         Horizon's scroll-container.js patches pushState to stash the scroll
         position first. */
      history.pushState({ urlParameters: new URL(href, window.location.href).search.replace(/^\?/, '') }, '', href);

      var triggerId = this.openTrigger ? this.openTrigger.id : null;
      var focusName = control ? control.getAttribute('name') : null;
      var focusValue = control ? control.value : null;

      var self = this;
      renderResults(href, { restoreControls: true }).then(function (landed) {
        if (!landed) return;

        self._reassertOpenState(triggerId, focusName, focusValue);

        if (!closedTriggerId) return;
        var active = document.activeElement;
        if (active && active !== document.body && active.isConnected) return;
        var trigger = document.getElementById(closedTriggerId);
        if (trigger) trigger.focus();
      });
    }

    /* The response is always rendered with every panel closed, and a morph may
       replace the focused checkbox. So the open panel and the keyboard
       position are re-established by id and name + value after each render.
       `keepFocus` stops the re-open from moving focus to the close button. */
    _reassertOpenState(triggerId, name, value) {
      if (!triggerId) return;

      var trigger = document.getElementById(triggerId);
      if (!trigger || !this.contains(trigger)) return;

      this.openTrigger = null;
      this.openPanel(trigger, { keepFocus: true });

      if (!name) return;

      var panel = this.panelFor(trigger);
      if (!panel) return;

      var byName = '[name="' + CSS.escape(name) + '"]';
      var control =
        (value ? panel.querySelector(byName + '[value="' + CSS.escape(value) + '"]') : null) || panel.querySelector(byName);
      if (control && document.activeElement !== control) control.focus();
    }

    _trapFocus(event) {
      a11y.trapFocus(this.openTrigger && this.panelFor(this.openTrigger), event);
    }
  }

  customElements.define('rtb-filter-bar', RtbFilterBar);

  /* ==========================================================================
     Back / forward
     ========================================================================== */

  window.addEventListener('popstate', function () {
    var root = collectionRoot();
    if (!root) return;

    /* An open sheet holds the scroll lock; the re-render would close its panel
       under it and leave the page unscrollable. */
    toArray(root.querySelectorAll('rtb-filter-bar')).forEach(function (bar) {
      bar.closeOpenPanel({ focusTrigger: false });
    });

    /* Already on screen: a scroll-position state entry, a fragment-only
       entry, or the entry the last render produced. */
    if (normalizeUrl(window.location.href) === renderedUrl) return;

    renderResults(window.location.href, { restoreControls: true });
  });

  /* ==========================================================================
     Add to cart. Delegated on `document`, because every section render
     replaces every card.
     ========================================================================== */

  var buttonState = new WeakMap();

  /**
   * Opens Horizon's quick-add modal for a multi-option product. The card is not
   * a <product-card>, so a hidden <quick-add-component> is created per button
   * and handed the product URL; this is the one place RTB drives Horizon's
   * quick-add internals, so re-check it on every Horizon upgrade. Without the
   * modal (quick add switched off) or the module, the button goes to the
   * product page.
   */
  function routeToQuickAdd(button, event) {
    var productUrl = button.getAttribute('data-product-url') || '';

    if (!document.getElementById('quick-add-dialog')) {
      if (productUrl) window.location.assign(productUrl);
      return;
    }

    loadModule('@theme/quick-add').then(function (module) {
      if (!module) {
        if (productUrl) window.location.assign(productUrl);
        return;
      }

      var host = stateFor(button).quickAdd;
      if (!host || !host.isConnected) {
        host = document.createElement('quick-add-component');
        host.style.display = 'none';
        Object.defineProperty(host, 'productPageUrl', { value: productUrl, configurable: true });
        host.dataset.productTitle = button.getAttribute('data-product-title') || '';
        (button.parentNode || document.body).appendChild(host);
        stateFor(button).quickAdd = host;
      }

      if (typeof host.handleClick === 'function') host.handleClick(event);
      else if (productUrl) window.location.assign(productUrl);
    });
  }

  function stateFor(button) {
    var state = buttonState.get(button);
    if (!state) {
      state = { busy: false, addedTimer: 0, errorTimer: 0 };
      buttonState.set(button, state);
    }
    return state;
  }

  function errorElementFor(button) {
    var id = button.getAttribute('aria-describedby');
    var byId = id ? document.getElementById(id) : null;
    if (byId) return byId;
    var card = button.closest('.rtb-catalogue-card');
    return card ? card.querySelector('.rtb-catalogue-card__error') : null;
  }

  /* Busy is `aria-disabled`, never `disabled`: `disabled` mid-interaction
     drops focus to <body> and loses the shopper's place in the grid. */
  function setBusy(button, busy) {
    stateFor(button).busy = busy;

    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      var adding = cartLabel(button, 'adding');
      if (adding && !button.querySelector('[data-rtb-adding]')) {
        var note = document.createElement('span');
        note.className = 'rtb-visually-hidden visually-hidden';
        note.setAttribute('data-rtb-adding', '');
        note.textContent = ' ' + adding;
        button.appendChild(note);
      }
      return;
    }

    button.removeAttribute('aria-disabled');
    var existing = button.querySelector('[data-rtb-adding]');
    if (existing) existing.remove();
  }

  /* The error element is role="alert" and hidden by `:empty`, so writing text
     into it both reveals and announces it. */
  function showCardError(button, message) {
    var target = errorElementFor(button);
    if (!target) return;

    var state = stateFor(button);
    if (state.errorTimer) window.clearTimeout(state.errorTimer);

    target.textContent = message;
    state.errorTimer = window.setTimeout(function () {
      if (target.isConnected) target.textContent = '';
    }, ERROR_MS);
  }

  function clearCardError(button) {
    var target = errorElementFor(button);
    if (target) target.textContent = '';
  }

  /* `data-added` drives Horizon's add-to-cart keyframes
     (snippets/add-to-cart-button-styles.liquid). */
  function playAddedBeat(button) {
    var state = stateFor(button);
    if (state.addedTimer) window.clearTimeout(state.addedTimer);

    button.dataset.added = 'true';
    state.addedTimer = window.setTimeout(function () {
      button.removeAttribute('data-added');
      state.addedTimer = 0;
    }, ADDED_MS);
  }

  /* Section ids for the cart response, from the live cart-items components.
     Capped at five: more fails the whole request with a 400. */
  function cartSectionIds() {
    var ids = [];
    toArray(document.querySelectorAll('cart-items-component')).forEach(function (component) {
      var id = component.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.slice(0, 5).join(',');
  }

  function fetchCart() {
    return fetch(Theme.routes.cart_url + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (response) {
      if (!response.ok) throw new Error('Failed to fetch cart: ' + response.status);
      return response.json();
    });
  }

  /* Without @shopify/events the cart bubble and drawer still need to hear
     about the add. Horizon's listeners read `event.promise`, so the plain
     event carries a resolved one in the same shape. */
  function dispatchPlainCartUpdate(button, sections) {
    return fetchCart()
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        var event = new Event(EVT_CART_LINES_UPDATE, { bubbles: true });
        event.promise = Promise.resolve({
          cart: cart ? { totalQuantity: cart.item_count } : null,
          detail: {
            didError: false,
            items: cart ? cart.items : [],
            source: 'rtb-catalogue-card',
            itemCount: cart ? cart.item_count : 0,
            sections: sections,
          },
        });
        button.dispatchEvent(event);
      });
  }

  function addToCart(button) {
    var state = stateFor(button);
    if (state.busy) return;

    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    var status = cartStatusRegion(button);
    var title = button.getAttribute('data-product-title') || '';
    var errorLabel = cartLabel(button, 'error');

    clearCardError(button);
    setBusy(button, true);

    loadModule('@shopify/events').then(function (events) {
      var CartLinesUpdateEvent = events && events.CartLinesUpdateEvent;
      var deferred = CartLinesUpdateEvent ? CartLinesUpdateEvent.createPromise() : null;

      if (CartLinesUpdateEvent) {
        /* Dispatched before the fetch with a deferred promise: the handshake
           cart-icon.js, component-cart-items.js, cart-drawer.js and
           rtb-header.js await. */
        button.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: deferred.promise,
          })
        );
      }

      function settle(detail) {
        return fetchCart().then(function (cart) {
          detail.items = cart.items;
          detail.source = 'rtb-catalogue-card';
          detail.itemCount = 1;
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: detail });
        });
      }

      var sections = cartSectionIds();
      var payload = { items: [{ id: Number(variantId), quantity: 1 }] };
      if (sections) payload.sections = sections;

      fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (response) {
          /* /cart/add.js answers a rejection with a truthy `status` (422) and a
             success with none; truthiness is what Horizon tests too. */
          if (response && response.status) {
            var message = response.description || response.message || errorLabel;
            showCardError(button, message);
            a11y.announce(status, message);

            /* Resolve with didError rather than reject, so every listener
               settles on the real cart. */
            if (deferred) {
              return settle({ didError: true }).catch(function () {
                deferred.reject(new Error(message));
              });
            }
            return;
          }

          playAddedBeat(button);

          var added = cartLabel(button, 'added');
          a11y.announce(status, (title ? title + ' — ' : '') + added);
          window.setTimeout(function () {
            if (status && status.isConnected && added && status.textContent.indexOf(added) !== -1) status.textContent = '';
          }, SUCCESS_MS);

          if (!deferred) return dispatchPlainCartUpdate(button, response.sections);

          return settle({ didError: false, sections: response.sections }).catch(function (error) {
            deferred.reject(error);
          });
        })
        .catch(function (error) {
          // Transport failure: inline on the card, polite in the region, no navigation.
          showCardError(button, errorLabel);
          a11y.announce(status, errorLabel);
          if (deferred) deferred.reject(error);
          debug('add to cart failed', error);
        })
        .then(function () {
          setBusy(button, false);
        });
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-rtb-add]');
    if (!button) return;

    event.preventDefault();

    if (button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;

    /* A product with real options opens Horizon's quick-add modal; the card
       never posts `selected_or_first_available_variant` for it. */
    if (button.hasAttribute('data-needs-options')) {
      routeToQuickAdd(button, event);
      return;
    }

    addToCart(button);
  });
})();
